import Errors from "../../utils/Errors"
import {
    changeFieldProperty,
    setFieldListOptions,
    setFieldVisibility
} from "../../../apps/KpModule/actions"
import {getDataListList} from "../../../apps/KpModule/selectors"
import {getFieldModuleForm} from './themeConfig'

const _ = require("lodash");

function updateFieldsOptions({themeConfig, store}) {
    const state = store.getState()

    const fieldsByDatalist = {
        'm-S-subTheme.ThemeCondition_source': 'e_conditions.e_source',
        'm-S-subTheme.ThemeCondition_source2': 'e_conditions.e_source2',
        'm-S-subTheme.SubThemeElement_codeSource': 'e_subThemeElements.e_codeSource'
    }

    Object.keys(fieldsByDatalist).forEach(dataList => {
        const options = getDataListList(state, dataList)

        const filteredOptions = options.filter(
            option => _.get(option, 'inputCorrespondenceByImportType.id') === _.get(themeConfig, 'stream.id')
        )

        store.dispatch(
            setFieldListOptions(fieldsByDatalist[dataList], _.map(filteredOptions, "id"))
        );
    })
}

export const dependencies = [
    {
        name: "SubThemeElement",
        facets: ["translatedField"],
        fields: [
            {
                path: "code",
                ps: {object: ["mongoFieldName"]},
                notEmpty: true,
                index: true
            },
            "name",
            {
                path: "codeSource",
                type: "InputCorrespondence",
                link: "MTO"
            },
            "value",
            {
                path: "completeCode",
                fieldPath: ["code", "subTheme.completeCode"],
                lazy: true,
                $f: function(object, context, callback) {
                    return callback(null, `${object.subTheme.completeCode}.${object.code}`)
                }
            }
        ],
        ps: {
            delete: [
                {
                    $$v: async function (object, callback) {
                        try {
                            const [alertConfiguration, scoringConfig] = await Promise.all([
                                global.app.S.AlertConfiguration.collection.findOne({$or: [
                                        {'alertFields.themeJoin': new global.ObjectID(object.id)},
                                        {'alertFields.denominatorTheme': new global.ObjectID(object.id)},
                                    ]}),
                                global.app.S.ScoringConfig.collection.findOne({$or: [
                                        {'scoringFields.numeratorTheme': new global.ObjectID(object.id)},
                                        {'scoringFields.denominatorTheme': new global.ObjectID(object.id)},
                                    ]}),
                            ]);

                            if(alertConfiguration) throw new Errors.ValidationError(this.options.context.tc('cannotDeleteThemeUsedInAlertConfiguration'));
                            if(scoringConfig) throw new Errors.ValidationError(this.options.context.tc('cannotDeleteThemeUsedInScoringConfiguration'));

                            await global.db.collection('slp.elemData').updateMany(
                                {[`theme-${object.code}`]:{$exists: true}},
                                {$unset: {[`theme-${object.code}`]: ''}}
                            );

                            callback();
                        } catch (e) {
                            callback(e);
                        }
                    }
                }
            ]
        }
    }
]

export const entity = {
    name: "SubTheme",
    facets: ["translatedField"],
    filters: [
        {
            name: "isDormant",
            path: "statuses",
            object: "ObjectStatus",
            display: "name",
            isDefault: false,
            type: "tags",
            client: true,
            default: [{id: "active"}],
            query: function(context) {
                const statuses = _.get(context, "data.statuses") || []

                if(!statuses.length) return {}
                if(statuses.length === 2) return {}

                const status = statuses[0]
                return {dormant: status.id === "suspended"}
            }
        }
    ],
    fields: [
        {
            path: "code",
            ps: {object: ["mongoFieldName"]},
            notEmpty: true,
            index: true,
            unique: true,
            uniqueWith: "themeConfig"
        },
        "name",
        {
            type: "ThemeConfig",
            link: {
                type: "MTO",
                oppositeField: {
                    path: "subThemes"
                }
            }
        },
        {
            type: "SubThemeElement",
            link: {
                type: "OTM",
                /*
                oppositeField: {
                    path: "subTheme"
                }
                 */
            }
        },
        {path: "dormant", type: "boolean"},
        {path: "conditions", type: "ThemeCondition", link: "OTM"},
        {
            path: "completeCode",
            fieldPath: ["themeConfig.completeCode"],
            lazy: true,
            $f: function(object, context, callback) {
                return callback(null, `${object.themeConfig.completeCode}.${object.code}`)
            }
        },
        {
            path: "subThemeElementsNumber",
            fieldPath: ["subThemeElements.id"],
            f: function() {
                return this.subThemeElements.length
            }
        },
        {
            path: 'hasCalculatedData',
            fieldPath: ["themeConfig.code"],
            $f: async (subTheme, context, callback) => {
                try {
                    const theme = await global.app.S.ElemDataLine.collection
                        .findOne(
                            {[`theme-${subTheme.themeConfig.code}.${subTheme.code}`]: { $exists: true}});
                    callback(null, !!theme)
                } catch (e) {
                    callback(e);
                }
            }
        }
    ],
    nameCannotContainHyphens: (newObject) => {
        const containsHyphens = newObject.name.indexOf('-') !== -1
        if(containsHyphens) throw new Errors.ValidationError('nameCannotContainHyphens')
        const subThemeElementContainsHyphen = newObject.subThemeElements.some(subThemeElem => subThemeElem.name.indexOf('-') !== -1)
        if(subThemeElementContainsHyphen) throw new Errors.ValidationError('subThemeElementNameCannotContainHyphens')
    },
    uniqueSubThemeElements: (newObject) => {
        const codes = newObject.subThemeElements
            .map(elem => elem.code)
        const duplicated = codes.some((code, index) => codes.indexOf(code) !== index)
        if(duplicated) throw new Errors.ValidationError('subThemeElementsShouldBeUnique')
    },
    canNotDeleteUsedSubThemeElement: async (object, oldObject, context) => { // Made async, removed callback
        const action  = context.restAction && context.restAction.crudType
        if(action === 'C') return // No-op for new objects
        const deletedSubThemeElements = oldObject.subThemeElements.filter(e => !object.subThemeElements.some(o => o.id === e.id))

        await Promise.all(deletedSubThemeElements.map(async (subThemeElement) => { // async map
            try {
                const [alertConfiguration, scoringConfig] = await Promise.all([
                    global.app.S.AlertConfiguration.collection.findOne({$or: [
                            {'alertFields.themeJoin': new global.ObjectID(subThemeElement.id)},
                            {'alertFields.denominatorTheme': new global.ObjectID(subThemeElement.id)},
                        ]}),
                    global.app.S.ScoringConfig.collection.findOne({$or: [
                            {'scoringFields.numeratorTheme': new global.ObjectID(subThemeElement.id)},
                            {'scoringFields.denominatorTheme': new global.ObjectID(subThemeElement.id)},
                        ]}),
                ]);

                if(alertConfiguration) {
                    throw new Errors.ValidationError(context.tc('cannotDeleteSubThemeElementUsedInAlertConfiguration', {subThemeElementCode: subThemeElement.code}));
                }
                if(scoringConfig) {
                    throw new Errors.ValidationError(context.tc('cannotDeleteSubThemeElementUsedInScoringConfiguration', {subThemeElementCode: subThemeElement.code}));
                }
            } catch (e) {
                throw e; // Re-throw any caught errors
            }
        }));
    },
    cannotModifyUsedSubTheme: async function(object, oldObject, context) {
        const action  = context.restAction && context.restAction.crudType;
        if(action === 'C' || object.code === oldObject.code) return
        const theme = await global.app.S.ElemDataLine.collection.findOne(
            {[`theme-${oldObject.themeConfig.code}.${oldObject.code}`]: { $exists: true}}
        );
        if (!!theme) throw new Errors.ValidationError('cannotChangeUsedSubThemeCode')
    },
    cannotModifyUsedSubThemeElements: async (object, oldObject, context) => { // Made async, removed callback
        const action  = context.restAction && context.restAction.crudType;
        if(action === 'C') return;
        const deletedSubThemeElements = oldObject.subThemeElements.filter(e => !object.subThemeElements.some(o => o.code === e.code));

        await Promise.all(deletedSubThemeElements.map(async (subThemeElement) => { // async map
            const theme = await global.app.S.ElemDataLine.collection
                .findOne(
                    {[`theme-${oldObject.themeConfig.code}.${oldObject.code}.${subThemeElement.code}`]: { $exists: true}}
                );
            if (!!theme) throw new Errors.ValidationError('cannotChangeUsedSubThemeElementCode');
        }));
    },
    validateSave: async function(newObject, oldObject, context, callback) {
        try {
            await this.nameCannotContainHyphens(newObject, oldObject, context);
            await this.uniqueSubThemeElements(newObject, oldObject, context);
            await this.canNotDeleteUsedSubThemeElement(newObject, oldObject, context);
            await this.cannotModifyUsedSubTheme(newObject, oldObject, context);
            await this.cannotModifyUsedSubThemeElements(newObject, oldObject, context);
            callback();
        } catch (e) {
            callback(e);
        }
    },
    afterSave: async function(object, oldObject, context, callback) {
        const action  = context.restAction && context.restAction.crudType
        if(action === 'C') return callback()

        const deletedSubThemeElements = oldObject.subThemeElements.filter(e => !object.subThemeElements.some(o => o.code === e.code))
        try {
            await Promise.all(deletedSubThemeElements.map(async (subThemeElement) => {
                await global.db.collection('slp.elemData').updateMany(
                    {[`theme-${object.themeConfig.code}.${object.code}.${subThemeElement.code}`]:{$exists: true}},
                    {$unset: {[`theme-${object.themeConfig.code}.${object.code}.${subThemeElement.code}`]: ''}}
                );
            }));
            callback();
        } catch (e) {
            callback(e);
        }
    },
    ps: {
        delete: [
            {
                $$v: async function (object, callback) {
                    try {
                        const [themeConfig, alertConfiguration, scoringConfig] = await Promise.all([
                            global.app.S.ThemeConfig.collection.findOne({_id: object.themeConfig}),
                            global.app.S.AlertConfiguration.collection.findOne({$or: [
                                    {'alertFields.themeJoin': new global.ObjectID(object.id)},
                                    {'alertFields.denominatorTheme': new global.ObjectID(object.id)},
                                ]}),
                            global.app.S.ScoringConfig.collection.findOne({$or: [
                                    {'scoringFields.numeratorTheme': new global.ObjectID(object.id)},
                                    {'scoringFields.denominatorTheme': new global.ObjectID(object.id)},
                                ]}),
                        ]);

                        if(alertConfiguration) throw new Errors.ValidationError(this.options.context.tc('cannotDeleteSubThemeUsedInAlertConfiguration', {subThemeCode: object.code}));
                        if(scoringConfig) throw new Errors.ValidationError(this.options.context.tc('cannotDeleteSubThemeUsedInScoringConfiguration', {subThemeCode: object.code}));

                        await global.db.collection('slp.elemData').updateMany(
                            {group: new global.ObjectID(this.options.context?.group.id)},
                            {$unset: {[`theme-${themeConfig.code}.${object.code}`]: ''}}
                        );
                        callback();
                    } catch (e) {
                        callback(e);
                    }
                }
            }
        ]
    }}

export const module_ = {
    object: "SubTheme",
    tKey: "mTitle_subTheme",
    category: {
        path: 'setting',
        icon: 'settings'
    },
    deletionConfirmationMessage: 'subThemeDeletionConfirmationMessage',
    defaultSortBy : "code",
    defaultSortDirection : "ASC",
    viewMap: {
        dt: [
            {
                path: "themeConfig",
                tKey: "themeConfig",
                display: "tName",
                initiallyNotVisible: true
            },
            "code",
            {path: "name", translateName: true},
            {path: 'subThemeElementsNumber', tKey: 'subThemeElements'}
        ],
        form: {
            fields: [
                {
                    path: "themeConfig",
                    tKey: "themeConfig",
                    display: "tName",
                    fieldPath: ["id", "tName", "stream.id"],
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => {
                            updateFieldsOptions({themeConfig: newValue, store})
                        }
                    }
                },
                {
                    path: "code",
                    //editable: false
                },
                {path: "name", type: "textarea", required: true},
                {
                    path: "conditions",
                    tKey: "filteringConditions",
                    removable: true,
                    viewMap: {
                        dt: [
                            {path: "source", tKey: "cashierFileId"},
                            {path: "operator", translate: true},
                            {path: "source2", tKey: "cashierFileId2"},
                            {path: "condValue", tKey: "value"},
                            {path: "condValues", tKey: "values"}
                        ],
                        form: [
                            {
                                path: "conditionType",
                                type: "toggle",
                                sortList: false,
                                subscriptions: {
                                    onChange: (newValue, oldValue, {module, store}) => {
                                        const operatorField = getFieldModuleForm("conditions", "operator", module)
                                        const valueField = getFieldModuleForm('conditions', 'condValue', module)
                                        const valuesField = getFieldModuleForm('conditions', 'condValues', module)
                                        const compareToField = getFieldModuleForm('conditions', 'source2', module)

                                        const operator = operatorField.getValue()
                                        const showValuesField = !!operator && ['000000000000000000000018', '000000000000000000000019'].includes(operator.id)
                                        const isApplyConditionType = !!newValue && newValue.id === "applyCondition"
                                        const isApplyComparisonType = !!newValue && newValue.id === "applyComparison"

                                        store.dispatch(
                                            setFieldVisibility(
                                                valueField.id,
                                                isApplyConditionType && !showValuesField
                                            )
                                        )
                                        store.dispatch(
                                            setFieldVisibility(
                                                valuesField.id,
                                                isApplyConditionType && showValuesField
                                            )
                                        )

                                        if(isApplyConditionType) {
                                            store.dispatch(changeFieldProperty('e_conditions.e_source', 'tKey', 'cashierFileId'))
                                            compareToField.setValue(null)
                                        }

                                        if(isApplyComparisonType) {
                                            store.dispatch(changeFieldProperty('e_conditions.e_source', 'tKey', 'cashierFileId1'))
                                            valueField.setValue(null)
                                            valuesField.setValue(null)

                                            if(showValuesField) {
                                                operatorField.setValue(null)
                                            }
                                        }

                                        const defaultFields = ["source", "operator"]


                                        defaultFields.forEach(path => {
                                            store.dispatch(setFieldVisibility(`e_conditions.e_${path}`, !!newValue))
                                        })
                                        store.dispatch(setFieldVisibility(`e_conditions.e_source2`, isApplyComparisonType))

                                        if(!!newValue) {
                                            const state = store.getState()
                                            const options = getDataListList(
                                                state,
                                                'm-S-subTheme.ThemeCondition_operator'
                                            );
                                            const filteredOptions = options.filter(option => {
                                                if(newValue.id === "applyCondition") return true
                                                return !["000000000000000000000018", "000000000000000000000019"].includes(option.id)
                                            })

                                            store.dispatch(setFieldListOptions("e_conditions.e_operator", _.map(filteredOptions, "id")))
                                        }
                                    }
                                }
                            },
                            {path: "source", tKey: "cashierFileId", fieldPath: ['id', 'code', 'name', 'inputCorrespondenceByImportType.id'], hidden: true},
                            {
                                path: "operator",
                                translate: true,
                                hidden: true,
                                subscriptions: {
                                    onChange: (newValue, oldValue, {module, store}) => {
                                        const valueField = getFieldModuleForm('conditions', 'condValue', module)
                                        const valuesField = getFieldModuleForm('conditions', 'condValues', module)
                                        const conditionTypeField = getFieldModuleForm('conditions', 'conditionType', module)

                                        const conditionType = conditionTypeField.getValue()

                                        const isApplyConditionType = conditionType?.id === "applyCondition"

                                        const showValuesField = newValue && ['000000000000000000000018', '000000000000000000000019'].includes(newValue.id)

                                        store.dispatch(
                                            setFieldVisibility(
                                                valuesField.id,
                                                isApplyConditionType && showValuesField
                                            )
                                        )
                                        store.dispatch(
                                            setFieldVisibility(
                                                valueField.id,
                                                isApplyConditionType && !showValuesField
                                            )
                                        )

                                        if(!showValuesField) {
                                            valuesField.setValue(null)
                                        }

                                        if(showValuesField) {
                                            valueField.setValue(null)
                                        }
                                    }
                                },
                            },
                            {path: "condValue", tKey: "value", hidden: true},
                            {path: "condValues", tKey: "values", type: 'creatableTags', hidden: true},
                            {path: "source2", tKey: "cashierFileId2", fieldPath: ['id', 'code', 'name', 'inputCorrespondenceByImportType.id'], required: true, hidden: true}
                        ]
                    }
                },
                {
                    path: "subThemeElements",
                    tKey: "subThemeElements",
                    removable: true,
                    viewMap: {
                        dt: [
                            "code",
                            {path:"name", translateName: true},
                            {path: "codeSource", tKey: "cashierFileId"},
                            {path: "value"}
                        ],
                        form: [
                            {
                                path: "code",
                                editable: false
                            },
                            {path: "name", type: "textarea", required: true},
                            {path: "codeSource", tKey: "cashierFileId", fieldPath: ['id', 'code', 'name', 'inputCorrespondenceByImportType.id']},
                            {path: "value", required: true}
                        ]
                    }
                },
                { path: 'dormant', tKey: "suspend"},
                //{ path: 'hasCalculatedData', hidden: true }
            ],
            onOpen: ({ store }) => {
                const state = store.getState()
                /*
                const hasCalculatedData = _.get(state, 'edit.object.data.hasCalculatedData')
                const fields = ['e_code', 'e_themeConfig']
                fields.forEach(field => store.dispatch(changeFieldDisabled(field, hasCalculatedData)))
                store.dispatch(setFieldEdition('e_subThemeElements', !hasCalculatedData))
                 */
                const themeConfig = _.get(state, 'edit.object.data.themeConfig')

                updateFieldsOptions({themeConfig, store})
            }
        }
    },
    filters: [
        "isDormant",
        {
            path: "themeConfig",
            object: "ThemeConfig",
            client: true,
            display: "tName",
            clearable: false,
            query: function (context) {
                const themeConfigId = _.get(context, "data.themeConfig.id");
                if (themeConfigId) return {
                    "themeConfig": new global.ObjectID(themeConfigId)
                };
            }
        }
    ]
}
